import type { CustomAnswer } from "@scoach/types";
import { resolveCustomQuestions } from "@scoach/types";
import { Check } from "@scoach/ui/icons";

import { useAuthStore } from "../../../auth/store.ts";
import { useLiveMeetingStore } from "../store.ts";

// Hebrew / Arabic (+ presentation forms) → render the line right-to-left.
const RTL_RE = /[֐-׿؀-ۿ܀-ݏיִ-﷿ﹰ-﻿]/;
function isRtl(text: string): boolean {
  return RTL_RE.test(text);
}

/** An answer counts as "found" when it came from the meeting (not "not-found"). */
function isAnswered(a: CustomAnswer | undefined): boolean {
  return !!a && a.source !== "not-found" && a.answer.trim().length > 0;
}

/**
 * Live "My questions" tab: the rep's own custom questions (from Settings) with the
 * answers Sally has picked up from the conversation so far, and an X / Y progress
 * counter at the top. Answers are computed live by useLiveCustomSummary.
 */
export function CustomQuestionsLive() {
  const settings = useAuthStore((s) => s.user?.settings);
  // Use the per-meeting question set (role / meeting type) chosen at setup, matching what
  // useLiveCustomSummary answers — falling back to the user's default set.
  const customQuestionKey = useLiveMeetingStore((s) => s.meetingCustomQuestionKey);
  const questions = resolveCustomQuestions(customQuestionKey ?? undefined, settings);
  const answers = useLiveMeetingStore((s) => s.customSummary);

  const byQ = new Map<string, CustomAnswer>();
  for (const a of answers) byQ.set(a.question.trim().toLowerCase(), a);

  if (questions.length === 0) {
    return (
      <div className="fu-wrap">
        <p className="fu-intro" style={{ color: "var(--text-3)" }}>
          You haven't defined any custom questions yet. Add them in Settings → Custom summary and
          SellSynq will answer them live as the conversation unfolds.
        </p>
      </div>
    );
  }

  const answeredCount = questions.filter((q) => isAnswered(byQ.get(q.trim().toLowerCase()))).length;

  return (
    <div className="fu-wrap cq-wrap">
      <div className="cq-progress">
        <div className="cq-progress-head">
          <span className="cq-progress-label">Answered from the conversation</span>
          <span className="cq-progress-count mono">
            {answeredCount}/{questions.length}
          </span>
        </div>
        <div className="cq-progress-bar">
          <div
            className="cq-progress-fill"
            style={{ width: `${Math.round((answeredCount / questions.length) * 100)}%` }}
          />
        </div>
      </div>

      <ul className="cq-list">
        {questions.map((q, i) => {
          const a = byQ.get(q.trim().toLowerCase());
          const found = isAnswered(a);
          return (
            <li key={`cq-${i}-${q.slice(0, 24)}`} className={`cq-item ${found ? "cq-found" : ""}`}>
              <div className="cq-item-head">
                <div className={`cq-status ${found ? "on" : ""}`} aria-hidden>
                  {found ? <Check size={12} /> : String(i + 1).padStart(2, "0")}
                </div>
                <div className="cq-q" dir={isRtl(q) ? "rtl" : undefined}>
                  {q}
                </div>
              </div>
              <div className="cq-a" dir={a && isRtl(a.answer) ? "rtl" : undefined}>
                {found ? (
                  a?.answer
                ) : (
                  <span className="cq-a-pending">Not mentioned yet — listening…</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
